import { useCallback, useMemo, useState } from 'react';
import { createElement } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { DataTableColumn } from '@/shared/components/data-table';
import { TREATMENT_ACTIONS, ACTION_VARIANT, actionLabel } from '../labels';
import type { ExpiredLine } from '../types';

export type ExpiredLineColumn = DataTableColumn<ExpiredLine>;

const METHOD_BADGE_VARIANT: Record<ExpiredLine['methodBucket'], 'purple' | 'info' | 'secondary' | 'warning'> = {
  ONSITE: 'purple',
  DS18B20: 'info',
  MAIL: 'secondary',
  REVIEW: 'warning',
};

function buildColumns(t: TFunction): ExpiredLineColumn[] {
  return [
    {
      key: 'account',
      label: 'Account',
      sortKey: 'account',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search account...',
      getValue: (row) => row.account,
    },
    {
      key: 'siteName',
      label: 'Site',
      sortKey: 'siteName',
      filterType: 'text',
      getValue: (row) => row.siteName ?? '',
    },
    {
      key: 'sensor',
      label: 'Sensor',
      sortKey: 'sensor',
      filterType: 'text',
      getValue: (row) => row.sensor ?? '',
    },
    {
      key: 'channel',
      label: 'Channel',
      filterType: 'text',
      getValue: (row) => row.channel ?? '',
    },
    {
      key: 'probeSerial',
      label: 'Probe serial',
      sortKey: 'probeSerial',
      filterType: 'text',
      getValue: (row) => row.probeSerial ?? '',
    },
    {
      key: 'probeModel',
      label: 'Model',
      filterType: 'text',
      getValue: (row) => row.probeModel ?? '',
    },
    {
      key: 'methodBucket',
      label: 'Method',
      sortKey: 'methodBucket',
      filterType: 'select',
      filterAllLabel: 'All Methods',
      filterOptions: [
        { label: 'Onsite', value: 'ONSITE' },
        { label: 'DS18B20', value: 'DS18B20' },
        { label: 'Mail', value: 'MAIL' },
        { label: 'Review', value: 'REVIEW' },
      ],
      getValue: (row) => row.methodBucket,
      render: (row) =>
        createElement(
          Badge,
          { variant: METHOD_BADGE_VARIANT[row.methodBucket] },
          row.methodBucket,
        ),
    },
    {
      key: 'serviceMode',
      label: 'Service mode',
      sortKey: 'serviceMode',
      filterType: 'select',
      filterAllLabel: 'All Modes',
      filterOptions: [
        { label: 'Onsite', value: 'onsite' },
        { label: 'Mail', value: 'mail' },
      ],
      getValue: (row) => row.serviceMode ?? '',
      render: (row) =>
        row.serviceMode
          ? createElement(
              Badge,
              { variant: row.serviceMode === 'onsite' ? 'purple' : 'secondary' },
              row.serviceMode,
            )
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      // #1118 — the Expired tab had no treatment column at all, which is where it is
      // needed most: these probes are already overdue. Service mode says WHERE; this
      // says WHAT TO DO. Reuses the translated calibrationPlanning.action.* keys.
      key: 'action',
      label: t('calibrationPlanning.columns.action', 'Treatment'),
      sortKey: 'action',
      filterType: 'select',
      filterAllLabel: t('calibrationPlanning.columns.allActions', 'All treatments'),
      filterOptions: TREATMENT_ACTIONS.map((a) => ({ label: actionLabel(t, a), value: a })),
      getValue: (row) => row.action ?? '',
      render: (row) =>
        row.action
          ? createElement(Badge, { variant: ACTION_VARIANT[row.action] }, actionLabel(t, row.action))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'certNumber',
      label: 'Cert #',
      sortKey: 'certNumber',
      filterType: 'text',
      getValue: (row) => row.certNumber ?? '',
    },
    {
      key: 'dueDate',
      label: 'Due date',
      sortKey: 'dueDate',
      filterType: 'text',
      getValue: (row) => row.dueDate,
    },
    {
      key: 'daysOverdue',
      label: 'Days overdue',
      sortKey: 'daysOverdue',
      className: 'text-right tabular-nums',
      noFilter: true,
      getValue: (row) => String(row.daysOverdue),
    },
    {
      key: 'region',
      label: 'Region',
      sortKey: 'region',
      filterType: 'text',
      getValue: (row) => row.region ?? '',
    },
    {
      key: 'room',
      label: 'Room',
      filterType: 'text',
      getValue: (row) => row.room ?? '',
    },
    {
      key: 'maintenance',
      label: 'Maintenance',
      filterType: 'select',
      filterAllLabel: 'All',
      filterOptions: [
        { label: 'In maintenance', value: 'true' },
        { label: 'Not in maintenance', value: 'false' },
      ],
      getValue: (row) => (row.maintenance ? 'true' : 'false'),
      render: (row) =>
        row.maintenance
          ? createElement(Badge, { variant: 'gray' }, 'In Maintenance')
          : null,
    },
  ];
}

/**
 * Bespoke client-side sort + filter state for the "Expired calibrations"
 * drill-down panel. The full line list is already loaded in memory (comes
 * from the planning response), so this mirrors useSkuListTable's in-memory
 * filter/sort pattern rather than a server-paginated one.
 */
export function useExpiredLinesTable(lines: ExpiredLine[]) {
  const { t } = useTranslation('operations');
  const columns = useMemo(() => buildColumns(t), [t]);

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string>('daysOverdue');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSort = useCallback((key: string) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDirection((d) => (d === 1 ? -1 : 1));
        return prevKey;
      }
      setSortDirection(1);
      return key;
    });
  }, []);

  const clearFilters = useCallback(() => setColumnFilters({}), []);

  const filteredLines = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return lines;
    return lines.filter((line) =>
      active.every(([colKey, value]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col) return true;
        const raw = col.getValue(line, undefined);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(value) as string[];
            return selected.length === 0 || selected.includes(String(raw ?? ''));
          } catch {
            return true;
          }
        }
        return String(raw ?? '')
          .toLowerCase()
          .includes(value.toLowerCase());
      }),
    );
  }, [lines, columnFilters, columns]);

  const sortedLines = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey || c.key === sortKey);
    if (!col) return filteredLines;
    const dir = sortDirection;
    return [...filteredLines].sort((a, b) => {
      const va = col.getValue(a, undefined);
      const vb = col.getValue(b, undefined);
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && va !== '' && vb !== '') {
        if (na === nb) return 0;
        return na > nb ? dir : -dir;
      }
      if (va === vb) return 0;
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      return va > vb ? dir : -dir;
    });
  }, [filteredLines, sortKey, sortDirection, columns]);

  return {
    columns,
    columnFilters,
    setColumnFilter,
    sortKey,
    sortDirection,
    handleSort,
    clearFilters,
    sortedLines,
  };
}
